import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import React from 'react'
import * as THREE from 'three'

export const stopPropagation = (e: React.UIEvent) => {
  e.stopPropagation()
  if (e.nativeEvent.cancelable) {
    e.preventDefault()
  }
}

// Helper to parse "x,y,z" string to number array or null
const parseCommaSeparatedXYZString = (vecString: unknown): number[] | null => {
  if (typeof vecString !== 'string') return null
  const parts = vecString.split(',').map(Number)
  if (parts.length === 3 && !parts.some(isNaN)) {
    return parts
  }
  return null
}

interface DefaultCameraSettings {
  initial_position?: number[]
  initial_view_direction?: number[]
}

/**
 * Initializes the camera position and look-at target based on query parameters
 * and default settings.
 *
 * @param camera The THREE.PerspectiveCamera to modify.
 * @param queryParams An object containing potential 'cameraPos' and 'cameraTarget' strings.
 * @param defaultSettings An object with optional 'initial_position' and 'initial_view_direction' arrays.
 */
export const initializeCamera = (
  camera: THREE.PerspectiveCamera,
  queryParams: Record<string, unknown>,
  defaultSettings?: DefaultCameraSettings
) => {
  let initialPosition: number[] | undefined
  let lookAtTarget: number[] | undefined
  let positionFromQuery = false

  // 1. Determine Position
  const queryPos = parseCommaSeparatedXYZString(queryParams.cameraPos)
  if (queryPos) {
    initialPosition = queryPos
    positionFromQuery = true
  } else if (defaultSettings?.initial_position) {
    initialPosition = defaultSettings.initial_position
  } else {
    initialPosition = [0, 0, 5]
  }

  // 2. Determine Target
  const queryTarget = parseCommaSeparatedXYZString(queryParams.cameraTarget)
  if (queryTarget) {
    lookAtTarget = queryTarget
  } else if (!positionFromQuery && defaultSettings?.initial_view_direction) {
    // Only use default target if position is also from defaults
    lookAtTarget = defaultSettings.initial_view_direction
  }

  // 3. Apply to Camera
  if (initialPosition) {
    camera.position.set(
      initialPosition[0],
      initialPosition[1],
      initialPosition[2]
    )
  }

  if (lookAtTarget) {
    camera.lookAt(
      new THREE.Vector3(lookAtTarget[0], lookAtTarget[1], lookAtTarget[2])
    )
  }
  // If lookAtTarget is undefined, the camera keeps its default orientation
}

export const useObservationImages = (observationId?: string) => {
  const { fileRefs } = useRolodexFileLS({
    entry_id: observationId ?? '',
    path: '**',
  })

  const imageFileRefs = fileRefs.filter((ref) => {
    const ext = ref.path.split('.').pop()?.toLowerCase()
    return ext === 'jpg' || ext === 'jpeg' || ext === 'png'
  })

  const { urls: imageUrls } = useRolodexSignedURLs(
    imageFileRefs.map((ref) => ({
      entry_id: observationId ?? '',
      path: ref.path,
    }))
  )

  return imageUrls
}
